/**
 * Sound effects: animal voices, thumps, bells.
 *
 * A recording under `src/modules/suara/efek/<id>.<ext>` always wins. Until one
 * exists, a synthesised sketch stands in, so that Tebak Suara and Suara Siapa
 * Ini? are playable screens today rather than silent ones.
 *
 * The sketches are sketches. The spec says plainly that a synthesised purr does
 * not read as a purr to a child, and it is right - these exist so a teacher can
 * walk the lesson and a designer can judge the screen, not so a class can
 * learn from them. `#/suara` lists every effect still to record.
 *
 * Levels follow the spec's two warnings: the growl and the hiss are capped
 * below everything else here, in code rather than in the file, because a loud
 * growl on an 86-inch panel frightens a room of four-year-olds and a
 * frightened room cannot learn the safety lesson that follows.
 */

const recordings = import.meta.glob('../suara/efek/**/*.{mp3,ogg,m4a,wav,webm}', {
  query: '?url',
  import: 'default',
}) as Record<string, () => Promise<string>>;

function recordingFor(id: string): (() => Promise<string>) | undefined {
  const prefix = `../suara/efek/${id}.`;
  const path = Object.keys(recordings).find((p) => p.startsWith(prefix));
  return path ? recordings[path] : undefined;
}

/** Per-effect ceiling. Anything unlisted plays at 1. */
const CAP: Record<string, number> = {
  'anjing/geram': 0.45,
  'kucing/desis': 0.45,
};

let audio: AudioContext | null = null;

function context(): AudioContext | null {
  if (!audio) {
    const Ctor =
      window.AudioContext ??
      (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    audio = new Ctor();
  }
  if (audio.state === 'suspended') void audio.resume();
  return audio;
}

let noiseBuffer: AudioBuffer | null = null;

function noise(ctx: AudioContext): AudioBufferSourceNode {
  if (!noiseBuffer || noiseBuffer.sampleRate !== ctx.sampleRate) {
    noiseBuffer = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
    const data = noiseBuffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  }
  const source = ctx.createBufferSource();
  source.buffer = noiseBuffer;
  source.loop = true;
  return source;
}

interface Voice {
  ctx: AudioContext;
  out: GainNode;
  t: number;
}

function envelope(gain: GainNode, t: number, peak: number, attack: number, hold: number, release: number): void {
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(peak, t + attack);
  gain.gain.setValueAtTime(peak, t + attack + hold);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + attack + hold + release);
}

/** A pitched, filtered voice - the basis of meows, whines and barks. */
function cry(
  v: Voice,
  opts: { at: number; dur: number; from: number; peakHz: number; to: number; type?: OscillatorType; band: number; q?: number; gain: number; vibrato?: number },
): void {
  const { ctx, out } = v;
  const t = v.t + opts.at;
  const osc = ctx.createOscillator();
  osc.type = opts.type ?? 'sawtooth';
  osc.frequency.setValueAtTime(opts.from, t);
  osc.frequency.exponentialRampToValueAtTime(opts.peakHz, t + opts.dur * 0.35);
  osc.frequency.exponentialRampToValueAtTime(opts.to, t + opts.dur);

  if (opts.vibrato) {
    const lfo = ctx.createOscillator();
    const depth = ctx.createGain();
    lfo.frequency.value = 6;
    depth.gain.value = opts.vibrato;
    lfo.connect(depth).connect(osc.frequency);
    lfo.start(t);
    lfo.stop(t + opts.dur + 0.1);
  }

  const band = ctx.createBiquadFilter();
  band.type = 'bandpass';
  band.frequency.value = opts.band;
  band.Q.value = opts.q ?? 1.4;

  const gain = ctx.createGain();
  envelope(gain, t, opts.gain, 0.03, opts.dur * 0.5, opts.dur * 0.5);
  osc.connect(band).connect(gain).connect(out);
  osc.start(t);
  osc.stop(t + opts.dur + 0.1);
}

function hiss(v: Voice, opts: { at: number; dur: number; type: BiquadFilterType; hz: number; gain: number; am?: number; q?: number }): void {
  const { ctx, out } = v;
  const t = v.t + opts.at;
  const src = noise(ctx);
  const filter = ctx.createBiquadFilter();
  filter.type = opts.type;
  filter.frequency.value = opts.hz;
  filter.Q.value = opts.q ?? 0.8;
  const gain = ctx.createGain();
  envelope(gain, t, opts.gain, 0.04, opts.dur * 0.6, opts.dur * 0.4);

  let node: AudioNode = gain;
  if (opts.am) {
    // Amplitude flutter: what turns filtered noise into a purr or a growl.
    const trem = ctx.createGain();
    trem.gain.value = 0.5;
    const lfo = ctx.createOscillator();
    const depth = ctx.createGain();
    lfo.frequency.value = opts.am;
    depth.gain.value = 0.5;
    lfo.connect(depth).connect(trem.gain);
    lfo.start(t);
    lfo.stop(t + opts.dur + 0.1);
    gain.connect(trem);
    node = trem;
  }

  src.connect(filter).connect(gain);
  node.connect(out);
  src.start(t);
  src.stop(t + opts.dur + 0.1);
}

function bell(v: Voice, notes: number[], stagger = 0.12): void {
  notes.forEach((hz, i) => {
    const t = v.t + i * stagger;
    const osc = v.ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = hz;
    const gain = v.ctx.createGain();
    envelope(gain, t, 0.22, 0.01, 0.02, 0.9);
    osc.connect(gain).connect(v.out);
    osc.start(t);
    osc.stop(t + 1.1);
  });
}

const SKETCHES: Record<string, (v: Voice) => void> = {
  'kucing/meong': (v) =>
    cry(v, { at: 0, dur: 0.75, from: 480, peakHz: 820, to: 430, band: 1100, gain: 0.5, vibrato: 12 }),
  'kucing/meong-panjang': (v) =>
    cry(v, { at: 0, dur: 1.4, from: 420, peakHz: 760, to: 360, band: 1000, gain: 0.45, vibrato: 18 }),
  'kucing/dengkur': (v) => hiss(v, { at: 0, dur: 1.8, type: 'lowpass', hz: 260, gain: 0.7, am: 24 }),
  'kucing/desis': (v) => hiss(v, { at: 0, dur: 0.9, type: 'highpass', hz: 3200, gain: 0.3 }),

  'anjing/guk': (v) => {
    for (const at of [0, 0.28]) {
      cry(v, { at, dur: 0.16, from: 380, peakHz: 520, to: 260, band: 900, q: 0.9, gain: 0.6 });
      hiss(v, { at, dur: 0.12, type: 'bandpass', hz: 1200, gain: 0.25 });
    }
  },
  'anjing/rengek': (v) =>
    cry(v, { at: 0, dur: 1.1, from: 850, peakHz: 1300, to: 950, type: 'triangle', band: 1200, gain: 0.35, vibrato: 30 }),
  'anjing/geram': (v) => {
    cry(v, { at: 0, dur: 1.4, from: 95, peakHz: 110, to: 90, band: 380, q: 0.7, gain: 0.5 });
    hiss(v, { at: 0, dur: 1.4, type: 'lowpass', hz: 420, gain: 0.35, am: 32 });
  },
  'anjing/engah': (v) => {
    for (let i = 0; i < 6; i++) hiss(v, { at: i * 0.26, dur: 0.16, type: 'bandpass', hz: 1500, gain: 0.25 });
  },

  'ayam/petok': (v) => {
    [0, 0.16, 0.32, 0.6].forEach((at, i) =>
      cry(v, { at, dur: i === 3 ? 0.35 : 0.1, from: 520, peakHz: i === 3 ? 900 : 620, to: 480, type: 'square', band: 1300, gain: 0.3 }),
    );
  },

  'kelinci/hentak': (v) => {
    cry(v, { at: 0, dur: 0.18, from: 110, peakHz: 90, to: 45, type: 'sine', band: 120, q: 0.5, gain: 0.9 });
    hiss(v, { at: 0, dur: 0.08, type: 'lowpass', hz: 300, gain: 0.4 });
  },
  'kelinci/napas': (v) => hiss(v, { at: 0, dur: 1.6, type: 'bandpass', hz: 900, gain: 0.08, am: 1.2 }),

  'gerak/boing': (v) =>
    cry(v, { at: 0, dur: 0.35, from: 180, peakHz: 520, to: 600, type: 'sine', band: 500, q: 0.4, gain: 0.35, vibrato: 25 }),

  'lonceng/kucing': (v) => bell(v, [784, 988, 1175]),
  'lonceng/anjing': (v) => bell(v, [587, 740, 880, 1175], 0.09),
  'lonceng/kelinci': (v) => bell(v, [1047, 1319, 1568], 0.16),
};

/** True when a real recording exists for this id. */
export function isRecorded(id: string): boolean {
  return Boolean(recordingFor(id));
}

/** Ids that have a synthesised stand-in. */
export function sketchIds(): string[] {
  return Object.keys(SKETCHES);
}

export async function playEffect(id: string, options: { gain?: number } = {}): Promise<void> {
  const level = Math.min(options.gain ?? 1, CAP[id] ?? 1);
  const recording = recordingFor(id);

  if (recording) {
    try {
      const el = new Audio(await recording());
      el.volume = level;
      await el.play();
      return;
    } catch {
      // Autoplay refused or a bad file: fall through to the sketch.
    }
  }

  const sketch = SKETCHES[id];
  const ctx = context();
  if (!sketch || !ctx) return;

  const out = ctx.createGain();
  out.gain.value = level;
  out.connect(ctx.destination);
  sketch({ ctx, out, t: ctx.currentTime + 0.02 });
}
